<template>
  <div>
    <global-confirm-box ref="confirmBox"></global-confirm-box>
    <warehouse-return-dialog
      v-model="returnDialog"
      :edited-item="editedItem"
      :warehouse="filters.warehouse[0]"
      @groceriesReturned="addDocuments"
      @save="save"
      @success="onSuccess"
    >
    </warehouse-return-dialog>

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <template v-if="filteredDocuments.length > 0 || loading">
        <mobile-data-cards
          v-for="(group, warehouseId) in documentsByWarehouse"
          :key="warehouseId"
          :headers="mobileHeaders"
          :items="group.items"
          :loading="loading"
          :title="toLatin(group.warehouseName)"
          item-key="id"
          show-filters
          :fields-per-row="null"
          :show-row-number="false"
          :bold-fields="['number']"
        >
          <template v-slot:item.actions="{ item }">
            <v-row no-gutters justify="end">
              <circle-button
                @click="openDocument(item)"
                icon="mdi-eye"
                :tooltip="$t('overview')"
                color="primary"
                class="mr-2"
              />
              <remove-button @click="deleteItem(item)" />
            </v-row>
          </template>
        </mobile-data-cards>
      </template>

      <empty-state
        v-if="!loading && filteredDocuments.length === 0"
        icon="mdi-backup-restore"
        :title="$t('no_returns')"
        :subtitle="$t('no_returns_yet')"
      >
        <warehouse-button-return @click="openReturnDialog"></warehouse-button-return>
      </empty-state>

      <div class="fixed-bottom-button" v-if="filteredDocuments.length > 0">
        <v-btn color="pink" block @click="openReturnDialog" dark>
          <v-icon class="mr-2">mdi-backup-restore</v-icon>
          {{ $t("warehouse.return_goods") }}
        </v-btn>
      </div>
    </template>

    <!-- Desktop View -->
    <template v-else>
      <v-data-table
        v-if="filteredDocuments.length > 0 || loading"
        :headers="headers"
        :items="filteredDocuments"
        :loading="loading"
        class="ordera-box-shadow striped-table"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-spacer></v-spacer>
            <warehouse-button-return @click="openReturnDialog"></warehouse-button-return>
          </v-toolbar>
        </template>
        <template v-slot:item.warehouse.name="{ item }">
          {{ toLatin(item.warehouse?.name) || "-" }}
        </template>
        <template v-slot:item.document_date="{ item }">
          {{ localeDate(item.document_date) }}
        </template>
        <template v-slot:item.created_at="{ item }">
          {{
            localeDate(item.created_at, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-row no-gutters justify="end">
            <circle-button
              @click="openDocument(item)"
              icon="mdi-eye"
              :tooltip="$t('overview')"
              color="primary"
              class="mr-2"
            />
            <remove-button @click="deleteItem(item)" />
          </v-row>
        </template>
      </v-data-table>

      <empty-state
        v-if="!loading && filteredDocuments.length === 0"
        icon="mdi-backup-restore"
        :title="$t('no_returns')"
        :subtitle="$t('no_returns_yet')"
      >
        <warehouse-button-return @click="openReturnDialog"></warehouse-button-return>
      </empty-state>
    </template>
  </div>
</template>

<script>
import { WarehouseButtonReturn } from "@/components/warehouse/buttons";
import WarehouseReturnDialog from "@/components/warehouse/return/dialogs/WarehouseReturnDialog.vue";
import { returnDocumentGrocery } from "@/models/warehouse";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasWarehousePrinting from "@/mixins/warehouse/hasWarehousePrinting";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseService from "@/services/warehouse";

export default {
  components: {
    WarehouseButtonReturn,
    WarehouseReturnDialog,
    EmptyState,
    MobileDataCards,
  },
  mixins: [hasPrinting, hasWarehousePrinting, hasExcel, hasSnackbar],
  data: () => ({
    returnDialog: false,
    documents: [],
    editedItem: returnDocumentGrocery,
    loading: false,
  }),

  created() {
    this.load();
  },

  methods: {
    onSuccess() {
      this.load();
      this.$store.commit("warehouse/TRIGGER_REFRESH");
    },
    addDocuments(documents) {
      this.documents.push(...documents);
      this.showSuccessSnackbar(this.$t("return_documents_added"));
    },
    openReturnDialog() {
      this.editedItem = Object.assign(
        {},
        { ...returnDocumentGrocery, warehouse: this.filters.warehouse[0] }
      );
      this.returnDialog = true;
    },

    openDocument(item) {
      this.editedItem = Object.assign({}, item);
      this.returnDialog = true;
    },

    async deleteItem(item) {
      const confirmed = await this.$refs.confirmBox
        .open(
          this.$t("warehouse.delete_return"),
          this.$t("warehouse.confirm_delete_return"),
          {
            color: "warning",
          }
        )
        .catch(() => false);

      if (confirmed) {
        try {
          await warehouseService.removeWarehouse(item.id);
          this.documents = this.documents.filter((i) => i.id !== item.id);
          this.showSuccessSnackbar(this.$t("return_document_deleted"));
        } catch (err) {
          this.showErrorSnackbar(this.$t("error_deleting_return_document"));
          console.warn(err);
        }
      }
    },

    async save() {
      try {
        if (this.editedItem.id) {
          const res = await warehouseService.updateWarehouse(
            this.editedItem.id,
            this.editedItem
          );
          const index = this.documents.findIndex(
            (item) => item.id === this.editedItem.id
          );
          this.documents[index] = res.data;
          this.showSuccessSnackbar(this.$t("return_document_updated"));
        } else {
          const res = await warehouseService.createWarehouse(this.editedItem);
          this.documents.push(res.data);
          this.showSuccessSnackbar(this.$t("return_document_created"));
        }
      } catch (e) {
        this.showErrorSnackbar(this.$t("error_saving_return_document"));
      }
      this.returnDialog = false;
    },

    async load() {
      this.loading = true;
      try {
        const res = await warehouseService.getWarehouseDocuments({
          document_type: "return",
        });
        this.documents = res.data;
      } catch (e) {
        this.showErrorSnackbar(this.$t("error_loading_return_documents"));
      } finally {
        this.loading = false;
      }
    },
  },

  computed: {
    translatedDocuments() {
      return this.documents.map((document) => {
        return {
          ...document,
          number: this.translate(document.number),
          notes: this.translate(document.notes),
        };
      });
    },
    filteredDocuments() {
      return this.customSearch(
        this.translatedDocuments,
        this.headers,
        this.filters.search
      )
        .filter((item) => {
          const { from, to } = this.filters.dates;
          if (!from || !to) return true;

          const documentDate = new Date(item.document_date + "T12:00:00");
          const fromDate = new Date(from + "T00:00:00");
          const toDate = new Date(to + "T23:59:59");
          return documentDate >= fromDate && documentDate <= toDate;
        })
        .filter((item) => {
          if (this.filters.warehouse.length === 0) return true;
          return this.filters.warehouse.some(
            (warehouse) => warehouse.id === item.warehouse_id
          );
        });
    },
    filters() {
      return this.$store.state.warehouse.filters;
    },
    headers() {
      return [
        { text: "ID", value: "id", mobile: false },
        { text: this.$t("stock"), value: "warehouse.name", mobile: false },
        { text: this.$t("warehouse.document_number"), value: "number", cols: 6 },
        { text: this.$t("warehouse.return_date"), value: "document_date", cols: 6 },
        { text: this.$t("note"), value: "notes", cols: 9 },
        { text: this.$t("status_created"), value: "created_at", mobile: false, cols: 9 },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          print: false,
          cols: 3,
          width: "100px",
          align: "right",
        },
      ];
    },
    mobileHeaders() {
      return this.headers.filter((h) => h.mobile !== false);
    },
    documentsByWarehouse() {
      const groups = {};
      this.filteredDocuments.forEach((item) => {
        const warehouseId = item.warehouse_id;
        if (!groups[warehouseId]) {
          groups[warehouseId] = {
            warehouseName: item.warehouse?.name || this.$t("stock"),
            items: [],
          };
        }
        groups[warehouseId].items.push(item);
      });
      return groups;
    },
    exportTitle() {
      return [
        this.filters.warehouse.map((warehouse) => warehouse.name).join(", ") ||
          this.$t("stock"),
        this.$t("warehouse.write_off").toLowerCase(),
      ].join(" ");
    },
  },
  watch: {
    "$store.state.warehouse.refreshTrigger"() {
      this.load();
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName: this.exportTitle + ".xlsx",
          sheets: [
            {
              name: this.exportTitle,
              headers: this.headers,
              items: this.filteredDocuments,
              title: this.exportTitle,
              subtitle: this.subtitle,
            },
          ],
        });
      }
    },
    isPrinting(isPrinting) {
      if (isPrinting) {
        this.printDocument({
          title: this.exportTitle,
          subtitle: this.subtitle,
          tables: [
            {
              headers: this.headers,
              items: this.filteredDocuments,
            },
          ],
        });
      }
    },
  },
};
</script>

<style scoped>
.fixed-bottom-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px;
  background: white;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 5;
}
</style>
